import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import { AbsenceDialog } from './AbsenceDialog';
import api from '../../lib/api';
import type { Absence, Employee } from '../../types';
import { formatDate, getAbsenceTypeLabel, getStatusLabel } from '../../lib/utils';
import {
  Card, CardContent, CardHeader,
  Button, Badge, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@werkstatt/ui';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';

function getStatusBadgeVariant(status: string): 'success' | 'warning' | 'danger' {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'danger';
  return 'warning';
}

export default function Absences() {
  const queryClient = useQueryClient();
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAbsence, setEditAbsence] = useState<Absence | null>(null);

  const { data: employees = [] } = useQuery({
    queryKey: ['employees', true],
    queryFn: async () => {
      const res = await api.get<Employee[]>('/employees/?is_active=true');
      return res.data;
    },
  });

  const { data: absences = [], isLoading } = useQuery({
    queryKey: ['absences', employeeFilter, typeFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (employeeFilter && employeeFilter !== 'all') params.set('employee_id', employeeFilter);
      if (typeFilter && typeFilter !== 'all') params.set('absence_type', typeFilter);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      const res = await api.get<Absence[]>(`/absences/?${params}`);
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/absences/${id}`),
    onSuccess: () => {
      toast.success('Abwesenheit gelöscht');
      queryClient.invalidateQueries({ queryKey: ['absences'] });
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.put(`/absences/${id}/approve`, {}),
    onSuccess: () => {
      toast.success('Abwesenheit genehmigt');
      queryClient.invalidateQueries({ queryKey: ['absences'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => api.put(`/absences/${id}/reject`, {}),
    onSuccess: () => {
      toast.success('Abwesenheit abgelehnt');
      queryClient.invalidateQueries({ queryKey: ['absences'] });
    },
  });

  const employeeMap = Object.fromEntries(employees.map((e) => [e.id, e]));

  const calcDays = (start: string, end: string) => {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    return Math.round(ms / (1000 * 60 * 60 * 24)) + 1;
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold">Abwesenheiten</h1>
            <p className="text-muted-foreground mt-1">Urlaub, Krankheit & weitere Abwesenheiten</p>
          </div>
          <Button onClick={() => { setEditAbsence(null); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Abwesenheit erfassen
          </Button>
        </div>

        <Card variant="glass">
          <CardHeader>
            <div className="flex flex-wrap gap-3 items-center">
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Alle Mitarbeiter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Mitarbeiter</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.last_name}, {e.first_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Alle Typen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Typen</SelectItem>
                  <SelectItem value="vacation">Urlaub</SelectItem>
                  <SelectItem value="sick">Krankheit</SelectItem>
                  <SelectItem value="special">Sonderurlaub</SelectItem>
                  <SelectItem value="other">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Alle Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="pending">Ausstehend</SelectItem>
                  <SelectItem value="approved">Genehmigt</SelectItem>
                  <SelectItem value="rejected">Abgelehnt</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-muted-foreground text-sm py-8 text-center">Lade...</p>
            ) : absences.length === 0 ? (
              <p className="text-muted-foreground text-sm py-8 text-center">Keine Abwesenheiten gefunden</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-3 pr-4 font-medium">Mitarbeiter</th>
                      <th className="py-3 pr-4 font-medium">Von</th>
                      <th className="py-3 pr-4 font-medium">Bis</th>
                      <th className="py-3 pr-4 font-medium">Tage</th>
                      <th className="py-3 pr-4 font-medium">Typ</th>
                      <th className="py-3 pr-4 font-medium">Status</th>
                      <th className="py-3 pr-4 font-medium">Notiz</th>
                      <th className="py-3 font-medium">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {absences.map((a) => {
                      const emp = employeeMap[a.employee_id];
                      return (
                        <tr key={a.id} className="border-b hover:bg-muted/30">
                          <td className="py-3 pr-4 font-medium">
                            {emp ? `${emp.last_name}, ${emp.first_name}` : a.employee_id}
                          </td>
                          <td className="py-3 pr-4">{formatDate(a.start_date)}</td>
                          <td className="py-3 pr-4">{formatDate(a.end_date)}</td>
                          <td className="py-3 pr-4">{calcDays(a.start_date, a.end_date)}</td>
                          <td className="py-3 pr-4">{getAbsenceTypeLabel(a.absence_type)}</td>
                          <td className="py-3 pr-4">
                            <Badge variant={getStatusBadgeVariant(a.status)}>
                              {getStatusLabel(a.status)}
                            </Badge>
                          </td>
                          <td className="py-3 pr-4 max-w-32 truncate text-muted-foreground">{a.note || '—'}</td>
                          <td className="py-3">
                            <div className="flex items-center gap-1">
                              {a.status === 'pending' && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => approveMutation.mutate(a.id)}>
                                    <Check className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => rejectMutation.mutate(a.id)}>
                                    <X className="h-4 w-4 text-destructive" />
                                  </Button>
                                </>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => { setEditAbsence(a); setDialogOpen(true); }}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => {
                                if (confirm('Abwesenheit löschen?')) deleteMutation.mutate(a.id);
                              }}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AbsenceDialog open={dialogOpen} onClose={() => setDialogOpen(false)} absence={editAbsence} />
    </MainLayout>
  );
}
